import { useMutation, useSuspenseQuery } from '@apollo/client/react';
import {
  Button,
  DialogActionTrigger,
  DialogBody,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
  HStack,
  Link,
  Text,
  VStack,
} from '@chakra-ui/react';
import { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { CenteredDialogContent } from '#/components/ui/centered-dialog-content';
import { toaster } from '#/components/ui/toaster';
import { DELETE_UPLOAD } from '#/graphql/mutations/delete-upload';
import { RENAME_UPLOAD } from '#/graphql/mutations/rename-upload';
import { GET_UPLOADS_FOR_DOCUMENT } from '#/graphql/queries/document-uploads';

import { DocumentAttachFileModal } from './document-attach-file-modal';
import {
  DocumentUploadsTable,
  type DocumentUploadTableRow,
} from './document-uploads-table';

export type DocumentUploadsPanelVariant = 'page' | 'embedded';

export function DocumentUploadsPanel({
  variant,
  documentId,
  apiHost,
  userEmail,
  documentPath,
}: {
  variant: DocumentUploadsPanelVariant;
  documentId: string;
  apiHost: string;
  userEmail: string;
  /** Display path for `/documents/{path}/files` (embedded link). */
  documentPath: string;
}) {
  const { data, refetch } = useSuspenseQuery(GET_UPLOADS_FOR_DOCUMENT, {
    variables: { documentId, limit: 200 },
    fetchPolicy: 'cache-first',
  });
  const [uploadOpen, setUploadOpen] = useState(false);
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameFilename, setRenameFilename] = useState('');
  const [renameRowId, setRenameRowId] = useState<string | null>(null);
  const [deleteRow, setDeleteRow] = useState<DocumentUploadTableRow | null>(
    null,
  );

  const refetchUploads = useCallback(() => void refetch(), [refetch]);

  const [deleteUpload, { loading: deleteLoading }] = useMutation(
    DELETE_UPLOAD,
    {
      onCompleted: (result) => {
        if (!result.deleteUpload) {
          toaster.create({
            title: 'Could not delete',
            description: 'The file may have already been removed.',
            type: 'error',
          });
          setDeleteRow(null);
          return;
        }
        toaster.create({
          title: 'File removed',
          description: 'The upload is no longer listed.',
          type: 'success',
        });
        void refetch();
        setDeleteRow(null);
      },
      onError: (err) => {
        toaster.create({
          title: 'Delete failed',
          description: err.message,
          type: 'error',
        });
      },
    },
  );

  const [renameUpload, { loading: renameLoading }] = useMutation(
    RENAME_UPLOAD,
    {
      onCompleted: () => {
        toaster.create({
          title: 'Renamed',
          description: 'Filename updated.',
          type: 'success',
        });
        void refetch();
        setRenameOpen(false);
        setRenameRowId(null);
      },
      onError: (err) => {
        toaster.create({
          title: 'Rename failed',
          description: err.message,
          type: 'error',
        });
      },
    },
  );

  const rows = data?.uploadsForDocument ?? [];

  const openRename = useCallback((row: DocumentUploadTableRow) => {
    setRenameRowId(row.id);
    setRenameFilename(row.filename?.trim() ? row.filename! : '');
    setRenameOpen(true);
  }, []);

  const handleConfirmRename = useCallback(async () => {
    const trimmed = renameFilename.trim();
    if (!trimmed || !renameRowId) return;
    await renameUpload({
      variables: { id: renameRowId, filename: trimmed },
    });
  }, [renameFilename, renameRowId, renameUpload]);

  useEffect(() => {
    if (!renameOpen) {
      setRenameRowId(null);
      setRenameFilename('');
    }
  }, [renameOpen]);

  const emptyMessage =
    variant === 'page'
      ? 'No files linked to this document yet. Use Upload file… above or paste images into the editor.'
      : 'No files linked to this document yet. Use Upload file… below, paste images into the editor, or open the Files page to rename or remove uploads.';

  return (
    <VStack gap={4} align="stretch" width="100%">
      <HStack gap={2} flexWrap="wrap" align="center">
        {variant === 'page' ? (
          <Button size="sm" onClick={() => void refetch()}>
            Refresh
          </Button>
        ) : (
          <Link
            asChild
            color="blue.600"
            fontSize="sm"
            _hover={{ textDecoration: 'underline' }}
          >
            <RouterLink to={`/documents/${documentPath}/files`}>
              Manage on Files page
            </RouterLink>
          </Link>
        )}
        <Button
          size="sm"
          colorScheme="blue"
          onClick={() => setUploadOpen(true)}
        >
          Upload file…
        </Button>
      </HStack>
      <DocumentAttachFileModal
        open={uploadOpen}
        onOpenChange={setUploadOpen}
        apiHost={apiHost}
        userEmail={userEmail}
        documentId={documentId}
        onUploaded={refetchUploads}
      />
      <DocumentUploadsTable
        rows={rows}
        apiHost={apiHost}
        emptyMessage={emptyMessage}
        showFileManagement={variant === 'page'}
        onRename={
          variant === 'page'
            ? (row) => {
                openRename(row);
              }
            : undefined
        }
        onDelete={
          variant === 'page'
            ? (row) => {
                setDeleteRow(row);
              }
            : undefined
        }
      />

      <DialogRoot
        open={renameOpen}
        onOpenChange={(e) => {
          if (!e.open) setRenameOpen(false);
        }}
      >
        <CenteredDialogContent maxW="min(90vw, 28rem)">
          <DialogHeader>
            <DialogTitle>Rename file</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <Text fontSize="sm" color="fg.muted" mb={2}>
              Updates the display name only; the open link stays the same.
            </Text>
            <input
              value={renameFilename}
              onChange={(e) => setRenameFilename(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void handleConfirmRename();
              }}
              style={{ width: '100%' }}
            />
          </DialogBody>
          <DialogFooter>
            <HStack gap={2}>
              <DialogActionTrigger asChild>
                <Button variant="outline" onClick={() => setRenameOpen(false)}>
                  Cancel
                </Button>
              </DialogActionTrigger>
              <Button
                colorScheme="blue"
                loading={renameLoading}
                disabled={
                  !renameFilename.trim() || renameFilename.trim().length > 255
                }
                onClick={() => void handleConfirmRename()}
              >
                Save
              </Button>
            </HStack>
          </DialogFooter>
        </CenteredDialogContent>
      </DialogRoot>

      <DialogRoot
        open={deleteRow != null}
        onOpenChange={(e) => {
          if (!e.open) setDeleteRow(null);
        }}
      >
        <CenteredDialogContent maxW="min(90vw, 28rem)">
          <DialogHeader>
            <DialogTitle>Remove file</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <Text fontSize="sm">
              Remove “
              <Text as="span" fontWeight="semibold">
                {deleteRow?.filename?.trim() ? deleteRow.filename : 'this file'}
              </Text>
              ” from this document? The listing and open link will stop working;
              the stored bytes may remain in your bucket until purged
              separately.
            </Text>
          </DialogBody>
          <DialogFooter>
            <HStack gap={2}>
              <DialogActionTrigger asChild>
                <Button variant="outline" onClick={() => setDeleteRow(null)}>
                  Cancel
                </Button>
              </DialogActionTrigger>
              <Button
                colorScheme="red"
                loading={deleteLoading}
                disabled={!deleteRow}
                onClick={() => {
                  if (!deleteRow) return;
                  void deleteUpload({ variables: { id: deleteRow.id } });
                }}
              >
                Remove
              </Button>
            </HStack>
          </DialogFooter>
        </CenteredDialogContent>
      </DialogRoot>
    </VStack>
  );
}
